import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import {
    Accordion,
    ActionIcon,
    Anchor,
    Badge,
    Box,
    Button,
    Center,
    Group,
    Modal,
    Stack,
    Text,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core'
import {
    IconArrowRight,
    IconReportAnalytics,
    IconSearch,
    IconSend,
    IconUsers,
} from '@tabler/icons-react'
import { formatDistanceToNow } from 'date-fns'
import { DatasetType } from '@/lib/domain'
import type {
    AudienceConfiguration,
    AudienceThread,
    Dataset,
} from '@/lib/domain'
import { datasetIdToName } from '@/lib/datasetNames'
import { EmptyState } from '@/components/EmptyState'
import { AttentionBanner } from '@/components/AttentionBanner'
import { ListSkeleton } from '@/components/ListSkeleton'
import { segmentKey } from '@/features/segment/api'
import { useSegmentStubs } from '@/features/segment/queries'
import {
    SegmentDetail,
    type SegmentStage,
} from '@/features/segment/components/SegmentDetail'
import { useAudienceInventory } from '@/hooks/useAudienceInventory'
import { formatCompactNumber } from '@/lib/formatNumber'
import { matchColor } from '@/lib/matchColor'
import { AudienceSegmentRow } from './components/AudienceSegmentRow'
import { DistributeModal } from '@/features/segment/components/DistributeModal'

const SHAPE_NOUN: Record<DatasetType, string> = {
    [DatasetType.TRANSACTION]: 'transaction',
    [DatasetType.USER_ATTRIBUTE]: 'attribute',
    [DatasetType.CROSSED]: 'crossed',
}

const STATUS_META: Record<AudienceStatus, { label: string; color: string }> = {
    draft: { label: 'Draft', color: 'gray' },
    live: { label: 'Live', color: 'success' },
    attention: { label: 'Attention', color: 'warning' },
}

type AudienceStatus = 'draft' | 'live' | 'attention'

interface AudienceRollup {
    reach: number
    repMatch: number
    liveDestinations: string[]
    status: AudienceStatus
    makeup: string
    insights: { label: string; color: string }[]
    // Segments with no distribution at all, and segments whose distribution is
    // incomplete (a delivery still in needs-config). The page's core job is to
    // surface these so audiences can be finished and sent.
    undistributed: number
    incomplete: number
}

type DetailState = {
    config: AudienceConfiguration
    threadId: string
    stage: SegmentStage
    datasets: Dataset[]
} | null

type DistributeState = {
    threadId: string
    name: string
    segments: AudienceConfiguration[]
} | null

export function AudiencesPage() {
    const { audiences, isLoading } = useAudienceInventory()
    const { lifecycleFor, destinations } = useSegmentStubs()

    const [query, setQuery] = useState('')
    const [detail, setDetail] = useState<DetailState>(null)
    const [distribute, setDistribute] = useState<DistributeState>(null)

    // Per-audience rollups, recomputed when the inventory or the stub store
    // changes (lifecycleFor / destinations change identity on store updates).
    const rollups = useMemo(() => {
        const computeRollup = (a: AudienceThread): AudienceRollup => {
            const lifecycles = a.segments.map((c) =>
                lifecycleFor(segmentKey(c), a.threadId)
            )
            const reach = a.segments.reduce(
                (sum, c) => sum + (c.audience_size ?? 0),
                0
            )
            const repMatch = lifecycles.length
                ? Math.round(
                      lifecycles.reduce(
                          (sum, l) => sum + l.estimatedMatchPct,
                          0
                      ) / lifecycles.length
                  )
                : 0

            const liveIds = new Set<string>()
            for (const l of lifecycles) {
                for (const delivery of l.deliveries) {
                    const status = delivery.latestRun?.status
                    if (status === 'succeeded' || status === 'partial') {
                        liveIds.add(delivery.distribution.destinationId)
                    }
                }
            }
            const liveDestinations = [...liveIds].map(
                (id) => destinations.find((d) => d.id === id)?.platform ?? id
            )

            // Per-segment distribution gaps: none at all, or present but
            // incomplete (a delivery still needing setup).
            let undistributed = 0
            let incomplete = 0
            for (const l of lifecycles) {
                if (l.deliveries.length === 0) {
                    undistributed++
                } else if (
                    l.deliveries.some((d) => d.configState === 'needs-config')
                ) {
                    incomplete++
                }
            }

            const anyAttention = lifecycles.some(
                (l) => l.distributeStatus === 'attention'
            )
            const anyLive = lifecycles.some(
                (l) => l.distributeStatus === 'live'
            )
            const noneDistributed = lifecycles.every(
                (l) => l.deliveries.length === 0
            )
            const status: AudienceStatus = anyAttention
                ? 'attention'
                : anyLive
                  ? 'live'
                  : 'draft'

            // Counts by shape, e.g. "3 segments · 2 transaction · 1 crossed".
            const counts = new Map<DatasetType, number>()
            for (const c of a.segments) {
                counts.set(
                    c.dataset_type,
                    (counts.get(c.dataset_type) ?? 0) + 1
                )
            }
            const shapeParts = [...counts.entries()].map(
                ([type, n]) => `${n} ${SHAPE_NOUN[type]}`
            )
            const segLabel =
                a.segments.length === 1
                    ? '1 segment'
                    : `${a.segments.length} segments`
            const makeup = `${segLabel} · ${shapeParts.join(' · ')}`

            // Insights add what the status badge doesn't already say: the
            // 'attention' status covers the failed/needs-config case, so we
            // only surface low-match and the ready-to-distribute opportunity.
            const insights: { label: string; color: string }[] = []
            if (repMatch < 50)
                insights.push({ label: 'Low match', color: 'error' })
            if (noneDistributed)
                insights.push({ label: 'Ready to distribute', color: 'info' })

            return {
                reach,
                repMatch,
                liveDestinations,
                status,
                makeup,
                insights,
                undistributed,
                incomplete,
            }
        }

        return new Map(audiences.map((a) => [a.threadId, computeRollup(a)]))
    }, [audiences, lifecycleFor, destinations])

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return audiences
        return audiences.filter((a) => a.name.toLowerCase().includes(q))
    }, [audiences, query])

    // Audiences with a distribution gap (a segment with none, or an incomplete
    // one) — the headline of this screen, surfaced in a panel up top.
    const needsAction = useMemo(
        () =>
            filtered
                .map((a) => ({ a, r: rollups.get(a.threadId) }))
                .filter(
                    (x): x is { a: AudienceThread; r: AudienceRollup } =>
                        !!x.r && (x.r.undistributed > 0 || x.r.incomplete > 0)
                ),
        [filtered, rollups]
    )

    // Controlled accordion so the panel can expand + scroll to an audience.
    const [expanded, setExpanded] = useState<string[]>([])
    const reveal = (threadId: string) => {
        setExpanded((prev) =>
            prev.includes(threadId) ? prev : [...prev, threadId]
        )
        requestAnimationFrame(() => {
            document
                .getElementById(`aud-${threadId}`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
    }

    return (
        <Stack gap='lg' h='100%'>
            <Group
                justify='space-between'
                align='flex-start'
                style={{ flexShrink: 0 }}
            >
                <div>
                    <Title order={2}>Audiences</Title>
                    <Text c='dimmed'>
                        Your audiences and the segments that make them up.
                    </Text>
                </div>
                <TextInput
                    placeholder='Search audiences'
                    aria-label='Search audiences'
                    leftSection={<IconSearch size={16} />}
                    value={query}
                    onChange={(e) => setQuery(e.currentTarget.value)}
                    w={260}
                />
            </Group>

            {isLoading ? (
                <ListSkeleton />
            ) : audiences.length === 0 ? (
                <EmptyState
                    icon={<IconUsers size={28} />}
                    title='No audiences yet'
                    description="Build segments in a conversation and they'll show up here as an audience you can distribute."
                />
            ) : filtered.length === 0 ? (
                <Text c='dimmed' ta='center' py='xl'>
                    No audiences match “{query}”.
                </Text>
            ) : (
                <>
                    {needsAction.length > 0 && (
                        <AttentionBanner
                            title={
                                needsAction.length === 1
                                    ? '1 audience needs distribution setup'
                                    : `${needsAction.length} audiences need distribution setup`
                            }
                        >
                            <Stack gap={4} align='flex-start'>
                                {needsAction.map(({ a, r }) => (
                                    <Group
                                        key={a.threadId}
                                        gap='xs'
                                        wrap='nowrap'
                                    >
                                        <Anchor
                                            component='button'
                                            type='button'
                                            size='sm'
                                            onClick={() => reveal(a.threadId)}
                                        >
                                            {a.name}
                                        </Anchor>
                                        {r.undistributed > 0 && (
                                            <Badge
                                                size='xs'
                                                variant='light'
                                                color='info'
                                            >
                                                {r.undistributed} not
                                                distributed
                                            </Badge>
                                        )}
                                        {r.incomplete > 0 && (
                                            <Badge
                                                size='xs'
                                                variant='light'
                                                color='warning'
                                            >
                                                {r.incomplete} incomplete
                                            </Badge>
                                        )}
                                    </Group>
                                ))}
                            </Stack>
                        </AttentionBanner>
                    )}
                    <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        <Accordion
                            variant='separated'
                            multiple
                            value={expanded}
                            onChange={setExpanded}
                            styles={{
                                item: {
                                    backgroundColor:
                                        'var(--mantine-color-default)',
                                },
                            }}
                        >
                            {filtered.map((a) => {
                                const r = rollups.get(a.threadId)
                                if (!r) return null
                                const statusMeta = STATUS_META[r.status]
                                return (
                                    <Accordion.Item
                                        key={a.threadId}
                                        value={a.threadId}
                                        id={`aud-${a.threadId}`}
                                    >
                                        <Center>
                                            <Accordion.Control
                                                style={{ flex: 1 }}
                                            >
                                                <Group
                                                    justify='space-between'
                                                    wrap='nowrap'
                                                    gap='md'
                                                >
                                                    <Stack
                                                        gap={2}
                                                        style={{ minWidth: 0 }}
                                                    >
                                                        <Group
                                                            gap='xs'
                                                            wrap='nowrap'
                                                        >
                                                            <Text
                                                                fw={600}
                                                                truncate
                                                            >
                                                                {a.name}
                                                            </Text>
                                                            <Badge
                                                                size='sm'
                                                                variant='light'
                                                                color={
                                                                    statusMeta.color
                                                                }
                                                            >
                                                                {
                                                                    statusMeta.label
                                                                }
                                                            </Badge>
                                                            {r.insights.map(
                                                                (ins) => (
                                                                    <Badge
                                                                        key={
                                                                            ins.label
                                                                        }
                                                                        size='sm'
                                                                        variant='dot'
                                                                        color={
                                                                            ins.color
                                                                        }
                                                                    >
                                                                        {
                                                                            ins.label
                                                                        }
                                                                    </Badge>
                                                                )
                                                            )}
                                                        </Group>
                                                        <Text
                                                            size='xs'
                                                            c='dimmed'
                                                        >
                                                            {r.makeup} ·{' '}
                                                            {formatCompactNumber(
                                                                r.reach
                                                            )}{' '}
                                                            reach ·{' '}
                                                            <Text
                                                                span
                                                                c={matchColor(
                                                                    r.repMatch
                                                                )}
                                                            >
                                                                {r.repMatch}%
                                                                est. match
                                                            </Text>{' '}
                                                            · updated{' '}
                                                            {formatDistanceToNow(
                                                                new Date(
                                                                    a.lastUpdated
                                                                ),
                                                                {
                                                                    addSuffix: true,
                                                                }
                                                            )}
                                                        </Text>
                                                    </Stack>
                                                    <Group
                                                        gap='xs'
                                                        wrap='nowrap'
                                                    >
                                                        {r.liveDestinations
                                                            .length > 0 ? (
                                                            r.liveDestinations.map(
                                                                (p) => (
                                                                    <Badge
                                                                        key={p}
                                                                        variant='light'
                                                                        color='success'
                                                                    >
                                                                        {p}
                                                                    </Badge>
                                                                )
                                                            )
                                                        ) : (
                                                            <Text
                                                                size='xs'
                                                                c='dimmed'
                                                            >
                                                                Not distributed
                                                            </Text>
                                                        )}
                                                    </Group>
                                                </Group>
                                            </Accordion.Control>
                                            <Group
                                                gap='xs'
                                                wrap='nowrap'
                                                px='md'
                                            >
                                                <Button
                                                    size='sm'
                                                    leftSection={
                                                        <IconSend size={16} />
                                                    }
                                                    onClick={() =>
                                                        setDistribute({
                                                            threadId:
                                                                a.threadId,
                                                            name: a.name,
                                                            segments:
                                                                a.segments,
                                                        })
                                                    }
                                                >
                                                    Distribute
                                                </Button>
                                                <Tooltip label='Open thread'>
                                                    <ActionIcon
                                                        variant='subtle'
                                                        color='gray'
                                                        size='lg'
                                                        aria-label='Open thread'
                                                        component={Link}
                                                        to={`/thread/${a.threadId}`}
                                                    >
                                                        <IconArrowRight
                                                            size={18}
                                                        />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Group>
                                        </Center>
                                        <Accordion.Panel>
                                            <Stack gap='xs'>
                                                {a.segments.map((c) => (
                                                    <AudienceSegmentRow
                                                        key={segmentKey(c)}
                                                        config={c}
                                                        threadId={a.threadId}
                                                        onOpen={(
                                                            config,
                                                            stage
                                                        ) =>
                                                            setDetail({
                                                                config,
                                                                threadId:
                                                                    a.threadId,
                                                                stage,
                                                                datasets:
                                                                    a.datasets,
                                                            })
                                                        }
                                                    />
                                                ))}
                                            </Stack>
                                        </Accordion.Panel>
                                    </Accordion.Item>
                                )
                            })}
                        </Accordion>
                    </Box>
                </>
            )}

            <Modal
                opened={detail !== null}
                onClose={() => setDetail(null)}
                size='90%'
                title={
                    <Text size='xl' fw='bold'>
                        <IconReportAnalytics
                            style={{ verticalAlign: 'text-bottom' }}
                        />{' '}
                        {detail?.config.name || 'Segment details'}
                    </Text>
                }
                overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            >
                {detail && (
                    <SegmentDetail
                        config={detail.config}
                        threadId={detail.threadId}
                        names={datasetIdToName}
                        datasets={detail.datasets}
                        initialStage={detail.stage}
                        onClose={() => setDetail(null)}
                    />
                )}
            </Modal>

            {distribute && (
                <DistributeModal
                    threadId={distribute.threadId}
                    title={`Distribute “${distribute.name}”`}
                    segments={distribute.segments}
                    opened
                    onClose={() => setDistribute(null)}
                />
            )}
        </Stack>
    )
}
